import { createReducer, on } from '@ngrx/store';
import { ActiveComponentChanged, ResetStateFromRouter } from 'src/app/shared/actions/eliqua.actions';
import { v4 as uuidv4 } from 'uuid';
import {
  AttributeBoolValueChanged,
  CancelCAAddition,
  ClearAttributeDateValue,
  CouponSetAddAttribute,
  CouponSetTitleChanged,
  DeleteAttributeRow,
  ExistingAttributeDateChanged,
  ExistingBoolAttributeChanged,
  NewAttributeDateChanged,
  RegisterCouponSetAttributes,
  SaveAttribute,
  SaveValidAttribute,
  SelectedCodeChanged,
  SelectedTypeChanged,
  SelectedValueChanged,
  StringTableChanged,
  ValidFromDateFromChanged,
  ValidFromDateFromCleared,
  ValidUntilDateFromChanged,
  ValidUntilDateFromCleared,
} from '../actions/couponsets-attributes.actions';
import { CloseEditCouponsetSidenav, EditCouponset } from '../actions/summary.actions';
import { CouponsetsAttributes } from '../models/merchant.model';

const initialState: CouponsetsAttributes = {
  couponSetAttributes: null,
  couponSetAttributesValues: [],
  couponsetTitle: '',
  validFrom: new Date(0),
  validFromText: '',
  validUntil: new Date(0),
  validUntilText: '',
  displayedColumns: {},
  grid: '',
  selectedCode: '',
  selectedType: '',
  selectedValue: '',
  attributeDate: new Date(0),
  attributeDateText: '',
  attributeBoolValue: false,
  addAttribute: false,
  saveAttribute: false,
};

export const MerchantCouponsetsAttributesReducer = createReducer(
  initialState,
  on(EditCouponset, (state, { couponSet }) => {
    let grid: any = couponSet.attributes;
    grid = grid.map(
      (attribute: { attributeName: string; attributeValue: string; dataType: string }) => ({
        ...attribute,
        id: uuidv4(),
      }),
    );
    return {
      ...state,
      couponsetTitle: couponSet.couponSetTitle,
      validFrom: couponSet.validFromDate ? new Date(couponSet.validFromDate) : new Date(0),
      validFromText: couponSet.validFromDate,
      validUntil: couponSet.validUntilDate ? new Date(couponSet.validUntilDate) : new Date(0),
      validUntilText: couponSet.validUntilDate,
      displayedColumns: {
        Type: 'dataType',
        Code: 'attributeName',
        Value: 'attributeValue',
        Action: 'delete',
      },
      grid,
    };
  }),
  on(RegisterCouponSetAttributes, (state, { couponSetAttributes }) => ({
    ...state,
    couponSetAttributes,
    couponSetAttributesValues: couponSetAttributes.attributeCodes.map(
      (attributeCode) => attributeCode.attributeType,
    ),
  })),
  on(CouponSetAddAttribute, (state) => ({
    ...state,
    addAttribute: true,
  })),
  on(CancelCAAddition, (state) => ({
    ...state,
    addAttribute: false,
    selectedCode: '',
    selectedType: '',
    selectedValue: '',
    attributeDate: new Date(0),
    attributeDateText: '',
    attributeBoolValue: false,
    saveAttribute: false,
  })),
  on(SelectedTypeChanged, (state, { selectedType }) => ({
    ...state,
    selectedType,
    selectedCode: '',
    selectedValue: '',
    attributeDate: new Date(0),
    attributeDateText: '',
    attributeBoolValue: false,
  })),
  on(SelectedCodeChanged, (state, { selectedCode }) => ({
    ...state,
    selectedCode,
    selectedValue: '',
    attributeDate: new Date(0),
    attributeDateText: '',
    attributeBoolValue: false,
  })),
  on(SelectedValueChanged, (state, { selectedValue }) => ({
    ...state,
    selectedValue,
  })),
  on(NewAttributeDateChanged, (state, { attributeDate, attributeDateText }) => ({
    ...state,
    attributeDate,
    attributeDateText,
  })),
  on(AttributeBoolValueChanged, (state) => ({
    ...state,
    attributeBoolValue: !state.attributeBoolValue,
  })),
  on(ClearAttributeDateValue, (state) => ({
    ...state,
    attributeDate: new Date(0),
    attributeDateText: '',
  })),
  on(SaveAttribute, (state) => ({
    ...state,
    saveAttribute: true,
  })),
  on(SaveValidAttribute, (state) => {
    let gridCopy = [...state.grid];
    gridCopy.unshift({
      attributeName: state.selectedCode,
      attributeValue:
        state.selectedType === 'INTEGER' || state.selectedType === 'STRING'
          ? state.selectedValue
          : state.selectedType === 'BOOLEAN'
            ? state.attributeBoolValue
              ? 'true'
              : 'false'
            : state.attributeDateText,
      dataType: state.selectedType,
      id: uuidv4(),
    });
    return {
      ...state,
      grid: gridCopy,
    };
  }),
  on(StringTableChanged, (state, { text, index }) => {
    let gridCopy: any = [...state.grid];
    gridCopy[index] = { ...gridCopy[index], attributeValue: text };
    return {
      ...state,
      grid: gridCopy,
    };
  }),
  on(ExistingBoolAttributeChanged, (state, { index }) => {
    let gridCopy: any = [...state.grid];
    gridCopy[index] = {
      ...gridCopy[index],
      attributeValue: gridCopy[index].attributeValue === 'false' ? 'true' : 'false',
    };
    return {
      ...state,
      grid: gridCopy,
    };
  }),
  on(ExistingAttributeDateChanged, (state, { attributeDateText, index }) => {
    let gridCopy: any = [...state.grid];
    gridCopy[index] = { ...gridCopy[index], attributeValue: attributeDateText };
    return {
      ...state,
      grid: gridCopy,
    };
  }),
  on(DeleteAttributeRow, (state, { id }) => ({
    ...state,
    grid: state.grid.filter(
      (attribute: {
        attributeName: string;
        attributeValue: string;
        dataType: string;
        id: string;
      }) => attribute.id !== id,
    ),
  })),
  on(CouponSetTitleChanged, (state, { couponsetTitle }) => ({
    ...state,
    couponsetTitle,
  })),
  on(ValidFromDateFromCleared, (state) => ({
    ...state,
    validFrom: new Date(0),
    validFromText: '',
  })),
  on(ValidUntilDateFromCleared, (state) => ({
    ...state,
    validUntil: new Date(0),
    validUntilText: '',
  })),
  on(ValidFromDateFromChanged, (state, { validFrom, validFromText }) => ({
    ...state,
    validFrom,
    validFromText,
  })),
  on(ValidUntilDateFromChanged, (state, { validUntil, validUntilText }) => ({
    ...state,
    validUntil,
    validUntilText,
  })),
  on(ActiveComponentChanged, CloseEditCouponsetSidenav, () => initialState),
  on(ResetStateFromRouter, () => ({
    ...initialState
  }))
);
